import { useLazyQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { Accordion as AccordionBootstrap, Button, Card, Collapse, Form } from 'react-bootstrap';

import { GET_MONTH_ID_LIST } from '../../../graphql';
import MonthlyEditor, { MonthlyProgress } from './MonthlyEditor';
import MonthlyMissionsDisplay from './MonthlyMissionsDisplay';
import MonthlyPassRewardsDisplay from './MonthlyPassRewardsDisplay';

type SubSectionProps = {
  title: string;
  children: React.ReactNode;
};

/**
 * A collapsible block inside the card. Children stay mounted once opened, so results
 * already fetched survive collapsing — the same behaviour the two separate cards had.
 */
const SubSection = ({ title, children }: SubSectionProps) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="uv-subsection">
      <div
        className="uv-subsection-head"
        role="button"
        tabIndex={0}
        onClick={() => setOpen(!open)}
        onKeyPress={() => setOpen(!open)}
      >
        <span>{title}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} />
      </div>
      <Collapse in={open}>
        <div>
          <div className="uv-subsection-body">{children}</div>
        </div>
      </Collapse>
    </div>
  );
};

type MonthSelectProps = {
  monthIds: string[];
  value: string;
  onChange: (monthId: string) => void;
};

/**
 * Month picker for a single sub-section. Missions and level rewards are looked at for different
 * months (comparing this month's missions against a reward claimed months ago, say), so each one
 * owns its own selection instead of following a card-wide month.
 */
const MonthSelect = ({ monthIds, value, onChange }: MonthSelectProps) => {
  if (monthIds.length === 0) {
    return null;
  }

  return (
    <Form.Group>
      <Form.Label className="uv-subsection-label">Month</Form.Label>
      <Form.Control value={value} onChange={(e) => onChange(e.target.value)} as="select">
        {monthIds.map((monthId) => (
          <option key={monthId} value={monthId}>
            {monthId}
          </option>
        ))}
      </Form.Control>
    </Form.Group>
  );
};

type Props = {
  userId: string;
  // Passed straight through to the missions sub-section
  missions?: { id: string } | null;
  // Current monthly pass progress from the edit form. Edited here, but persisted by
  // "Save User" in the User Attributes card together with the rest of the form.
  currentMonthlyPass?: MonthlyProgress | null;
  onChangeMonthlyPass?: (monthly: MonthlyProgress) => void;
};

/**
 * One card for everything monthly-pass related on this user: the current progress editor plus a
 * sub-section each for missions and level rewards. The month list is fetched once here and shared,
 * but the selection is not - each sub-section picks its own month (see MonthSelect).
 */
const MonthlyPassDisplay = ({ userId, missions, currentMonthlyPass, onChangeMonthlyPass }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const [missionsMonthId, selectMissionsMonthId] = useState('');
  const [rewardsMonthId, selectRewardsMonthId] = useState('');

  const [getMonthIdList, { data: monthIds }] = useLazyQuery(GET_MONTH_ID_LIST, {
    variables: { input: { userId } },
  });

  useEffect(() => {
    if (userId !== undefined) {
      getMonthIdList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const availableMonthIds: string[] = monthIds?.app?.getUserMonthIdList?.list ?? [];

  useEffect(() => {
    if (availableMonthIds.length > 0) {
      selectMissionsMonthId(availableMonthIds[0]);
      selectRewardsMonthId(availableMonthIds[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthIds]);

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <AccordionBootstrap>
          <AccordionBootstrap.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
              Monthly pass
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </AccordionBootstrap.Toggle>
          <AccordionBootstrap.Collapse eventKey="0">
            <div className="mt-3">
              {availableMonthIds.length === 0 && (
                <div className="mb-2">There is no monthly pass progress available for this user</div>
              )}

              {/* Current progress is not tied to a month - it is whatever pass the user is on now,
                  edited through the same form as the rest of the User Attributes card. */}
              {currentMonthlyPass && onChangeMonthlyPass && (
                <div className="uv-subsection">
                  <div className="uv-subsection-head uv-subsection-head-static">
                    <span>Current progress</span>
                  </div>
                  <div className="uv-subsection-body">
                    <MonthlyEditor monthly={currentMonthlyPass} onChange={onChangeMonthlyPass} />
                  </div>
                </div>
              )}

              <SubSection title="Missions">
                <MonthSelect
                  monthIds={availableMonthIds}
                  value={missionsMonthId}
                  onChange={selectMissionsMonthId}
                />
                <MonthlyMissionsDisplay
                  userId={userId}
                  missions={missions}
                  monthId={missionsMonthId}
                  hasMonths={availableMonthIds.length > 0}
                />
              </SubSection>

              <SubSection title="Level rewards">
                <MonthSelect
                  monthIds={availableMonthIds}
                  value={rewardsMonthId}
                  onChange={selectRewardsMonthId}
                />
                <MonthlyPassRewardsDisplay userId={userId} monthId={rewardsMonthId} />
              </SubSection>
            </div>
          </AccordionBootstrap.Collapse>
        </AccordionBootstrap>
      </Card.Body>
    </Card>
  );
};

export default MonthlyPassDisplay;
